import { useState } from 'preact/hooks'
import { Plus, X } from 'lucide-preact'
import { cycleWeekIndex, dateKey } from '../lib/cycle'
import { scheduleOptions } from './schedule-text'
import type { MemberOption } from '../lib/members'
import type { NewChore, ScheduleKind } from '../types'

/**
 * "Ajouter une tâche": the one place a household-defined chore is created.
 *
 * The recurrence is expressed relative to the day being looked at — "seulement
 * le 25 août", "chaque mardi" — rather than as a weekday picker and a cycle
 * week, because the day is already on screen and choosing it twice is how a
 * chore ends up on the wrong one. `weekday`, `cycleWeek` and `onDate` are all
 * filled from that date whichever option is chosen; only the one the schedule
 * names is ever read (`src/types.ts`).
 *
 * It never touches the network and never awaits: the store marks the row dirty
 * and the sync engine carries it up ~2 s later, so a chore added on a train
 * exists immediately and reaches the other phone whenever it can — `CLAUDE.md`
 * §3.
 */

/** Shown when the user leaves the emoji blank, so no row renders with a gap. */
const FALLBACK_EMOJI = '✨'

/**
 * Every custom chore gets this category.
 *
 * Nothing branches on the value and nothing renders it; it is there so a future
 * grouping view has something to group by, and "perso" is the honest answer to
 * where these came from. Asking the user for it would be a fourth field on a
 * form whose whole point is to be finished in ten seconds.
 */
const CUSTOM_CATEGORY = 'perso'

/** "Toute la maison": every member gets the chore, and their own box to tick. */
const EVERYONE = ''

export function NewTaskForm({
  date,
  members,
  defaultAssigneeId,
  onAdd,
  onClose,
}: {
  date: Date
  members: readonly MemberOption[]
  /** The member whose list is on screen — the overwhelmingly likely answer. */
  defaultAssigneeId: string
  onAdd: (chore: NewChore) => void
  onClose: () => void
}) {
  const options = scheduleOptions(date)
  const [label, setLabel] = useState('')
  const [emoji, setEmoji] = useState('')
  const [schedule, setSchedule] = useState<ScheduleKind>('once')
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId)

  const trimmed = label.trim()

  // The day can move under the form — the date arrows sit right above it — and
  // the offered recurrences change with it. A chosen "un samedi sur quatre"
  // left standing after a step to Sunday would be a chore scheduled from a
  // cycle week the user never looked at, so it falls back to the one option
  // every day has.
  const active = options.some((option) => option.kind === schedule) ? schedule : 'once'

  // Same reasoning one step further: a member can be removed while this form is
  // open, and a chore assigned to a tombstone is a chore nobody ever sees.
  const assignee =
    assigneeId === EVERYONE || members.some((member) => member.id === assigneeId)
      ? assigneeId
      : EVERYONE

  function submit(event: Event) {
    event.preventDefault()
    if (!trimmed) return

    onAdd({
      label: trimmed,
      emoji: emoji.trim() || FALLBACK_EMOJI,
      category: CUSTOM_CATEGORY,
      assigneeId: assignee,
      schedule: active,
      weekday: date.getDay(),
      cycleWeek: cycleWeekIndex(date),
      onDate: dateKey(date),
    })

    onClose()
  }

  return (
    <form class="task-form" onSubmit={submit}>
      <div class="task-form-heading">
        <strong>Nouvelle tâche</strong>
        <button type="button" class="icon-button small" onClick={onClose} aria-label="Annuler">
          <X size={18} />
        </button>
      </div>

      <div class="task-form-row">
        <input
          class="emoji-input"
          type="text"
          value={emoji}
          maxLength={4}
          placeholder={FALLBACK_EMOJI}
          aria-label="Emoji"
          onInput={(event) => setEmoji((event.currentTarget as HTMLInputElement).value)}
        />
        <input
          type="text"
          value={label}
          maxLength={80}
          placeholder="Arroser les plantes"
          aria-label="Nom de la tâche"
          // autofocus so the keyboard is already up on a phone: the button that
          // opened this form was the user saying what they wanted to do next.
          autofocus
          onInput={(event) => setLabel((event.currentTarget as HTMLInputElement).value)}
        />
      </div>

      <label class="task-form-field">
        <span>Quand ?</span>
        <select
          value={active}
          onChange={(event) =>
            setSchedule((event.currentTarget as HTMLSelectElement).value as ScheduleKind)
          }
        >
          {options.map((option) => (
            <option value={option.kind}>{option.label}</option>
          ))}
        </select>
      </label>

      {/* Hidden while the household is one person, like the member chips: a
          picker with one real answer is a question nobody needs asked. */}
      {members.length > 1 && (
        <label class="task-form-field">
          <span>Pour qui ?</span>
          <select
            value={assignee}
            onChange={(event) =>
              setAssigneeId((event.currentTarget as HTMLSelectElement).value)
            }
          >
            <option value={EVERYONE}>Toute la maison</option>
            {members.map((member) => (
              <option value={member.id}>{member.displayName}</option>
            ))}
          </select>
        </label>
      )}

      <button class="primary" type="submit" disabled={!trimmed}>
        <Plus size={18} />
        Ajouter
      </button>
    </form>
  )
}
